import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { Box, Button, Chip, Stack, Typography, useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { CustomWidthTooltip } from "mui-toolkit/CustomWidthTooltip";
import DeleteConfirmationButton from "mui-toolkit/DeleteConfirmationButton";
import { formatPracticeName } from "utility/formatters";

import { NavigateIcon } from "../../../Icons/Icons";
import { CROP_YEAR_POTENTIAL } from "../helpers";

const getRowHeightAuto = () => "auto";

export default function ProducerFarmFieldDataTable({
  producerFarmFields,
  isLoading,
  onChange,
  pageSizes = [5, 10, 25, 50, 100],
  rowHeight = 80,
}) {
  const navigate = useNavigate();
  const { crop_year } = useParams();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const columns = useMemo(
    () => [
      {
        field: "fsa_tract_number",
        headerName: "Tract",
        flex: 1,
        renderCell: (params) =>
          crop_year === CROP_YEAR_POTENTIAL ? (
            params.value
          ) : (
            <Typography color="primary" component={Link} to={`./${params?.row?.id}`}>
              {params.value}
            </Typography>
          ),
      },
      {
        field: "fsa_field_number",
        headerName: "Field",
        flex: 1,
      },
      {
        field: "reported_acreage",
        headerName: "Acres",
        flex: 1,
      },
      {
        field: "CropProfiles_Sort",
        headerName: "Crop Profiles",
        flex: 1,
        sortable: false,
        display: "flex",
        renderCell: (params) => {
          const currentProducerFarmCrop = producerFarmFields.find((crop) => crop.id === params.id);
          return (
            <Box
              className="crop-profile-data-grid"
              sx={{
                width: "100%",
              }}
            >
              <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap", rowGap: 0, columnGap: 1 }}>
                {currentProducerFarmCrop?.CropProfiles?.map((currentCropProfile, index) => (
                  <Chip
                    key={index}
                    label={`${currentCropProfile?.commodity_name} ${formatPracticeName(currentCropProfile?.practice_name)}`}
                    sx={{
                      textOverflow: "ellipsis",
                      textTransform: "uppercase",
                      background: "transparent",
                      color: "text.secondary",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) =>
          [
            crop_year === CROP_YEAR_POTENTIAL && (
              <CustomWidthTooltip key="delete" title="Delete" placement="top" arrow>
                <DeleteConfirmationButton item={params.row} onChange={onChange} />
              </CustomWidthTooltip>
            ),
            crop_year !== CROP_YEAR_POTENTIAL && (
              <GridActionsCellItem
                key="view"
                icon={<NavigateIcon />}
                label="View Details"
                onClick={() => navigate(`./${params?.row?.id}`)}
              />
            ),
          ].filter(Boolean),
        flex: 0,
      },
    ],
    [producerFarmFields]
  );

  return (
    <>
      <TilleyDataGrid
        rows={producerFarmFields || []}
        columns={columns}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="producer-farm-fields"
        getRowId={(row) => row.id}
        getRowHeight={getRowHeightAuto}
        isLoading={isLoading}
        sx={{
          width: "99%",
          boxSizing: "border-box",
          "& .MuiDataGrid-row": {
            minHeight: rowHeight,
          },
          "& .MuiDataGrid-cell": {
            minHeight: rowHeight,
            display: "flex",
            alignItems: "center",
          },
        }}
      />
    </>
  );
}
